/**
 * /car-paint-code — the «how much paint / how to mix» section. Arithmetic in
 * calc.js (node-tested); this file draws the form and the answer.
 *
 * Every default is either a maker's number (DEFAULTS below, with the data
 * sheet it came from) or an estimate labelled as one on the page (the part
 * areas). The reader can overwrite each of them.
 */
import { parseRatio, mix, cupMarks, roundMl, sprayLitres, cansFor, layersFor } from './calc.js';

/* Approximate outer areas, m², of a mid-size passenger car's panels — an
   estimate from typical dimensions (hood ≈ 1.1 × 1.45 m, door skin
   ≈ 1.1 × 0.75 m …), NOT a maker's figure: the page says so and lets the
   reader correct the total. */
export const PARTS = [
    ['hood', 1.6], ['roof', 1.8], ['trunk', 1.0], ['door', 0.8], ['front_fender', 0.6],
    ['rear_fender', 0.8], ['bumper', 0.8], ['mirror', 0.06], ['sill', 0.3], ['spot', 0.05],
];

/* Makers' numbers (data sheets read 24.09.2026):
   - aerosol 400 ml ≈ 1.3 m² (Dupli-Color Auto Color Spray, presto clear);
     520 ml «до 2 м² при нанесении в один слой» (KUDO 1K metallic) — the
     calculator counts per coat and scales 1.3 m² with the can's volume;
     2–3 thin coats (KUDO), clear over metallic (Dupli-Color, KUDO);
   - 2K solid enamel 2:1 +10 %, 2 coats 50–70 µm, 475 m²/l at 1 µm → 47 %
     volume solids (Glasurit 22 Line HS);
   - basecoat 100:20, 10–20 µm, 145 m²/l at 1 µm → 14 % (Standoblue);
   - clear 100:50:0–15, 40–60 µm, 1 l per 8.1 m² at 50 µm → 40 % (Reoflex
     HS Clear Premium RX C-02);
   - HVLP transfer efficiency «has to exceed 65 %» (SATA).
   No paint or car maker publishes panel areas — PARTS is our estimate. */
export const DEFAULTS = {
    can: { sizes: [400, 520], size: 400, per400: 1.3, coats: 2, clearCoats: 2 },
    gun: {
        te: 65,
        enamel: { ratio: '2:1 +10%', vs: 47, dft: 55 },
        base: { ratio: '5:1', vs: 14, dft: 15 },
        mid: { ratio: '5:1', vs: 14, dft: 15 },
        clear: { ratio: '2:1', vs: 40, dft: 50 },
    },
};

/** One can's area per coat, scaled from the makers' 1.3 m² per 400 ml. */
export function canCover(ml) { return Math.round(DEFAULTS.can.per400 * ml / 400 * 10) / 10; }

const COMPONENTS = { enamel: ['paint', 'hardener', 'thinner'], clear: ['paint', 'hardener', 'thinner'], base: ['paint', 'thinner'], mid: ['paint', 'thinner'] };

let T = (k) => k, LOC = 'en', AE = () => {};
const st = { mode: 'can', qty: {}, area: null, system: 'enamel', can: { ...DEFAULTS.can, cover: canCover(DEFAULTS.can.size) },
    gun: JSON.parse(JSON.stringify(DEFAULTS.gun)), mix: { ratio: '2:1 +10%', pctOf: 'mix', by: 'need', amount: 300 } };

const esc = (s) => String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
/* Latin digits in every locale: the Arabic page's texts write 1.3 and 400,
   and a result in Arabic-Indic digits beside them reads as another number. */
const nf = (d) => new Intl.NumberFormat(LOC === 'ar' ? 'ar-u-nu-latn' : LOC, { maximumFractionDigits: d, minimumFractionDigits: 0 });
const num = (v, d) => nf(d == null ? 2 : d).format(v);
const parseNum = (s) => { const n = parseFloat(String(s == null ? '' : s).replace(',', '.')); return isFinite(n) ? n : NaN; };
const litres = (l) => (l < 1 ? num(roundMl(l * 1000), 0) + ' ' + T('calc.ml') : num(Math.round(l * 100) / 100, 2) + ' ' + T('calc.l'));

function partsArea() {
    let a = 0;
    for (const [id, area] of PARTS) a += (st.qty[id] || 0) * area;
    return Math.round(a * 100) / 100;
}
function area() { return st.area != null && st.area > 0 ? st.area : partsArea(); }

/* ── drawing ──────────────────────────────────────────────────────────────── */

function formHtml() {
    const parts = PARTS.map(([id, a]) => `<div class="cp-part">
            <span class="cp-part-n">${esc(T('calc.part.' + id))} <span class="cp-part-a">≈ ${num(a, 2)} ${esc(T('calc.m2'))}</span></span>
            <span class="cp-step"><button type="button" class="cp-step-b" data-dec="${id}" aria-label="${esc(T('calc.less') + ': ' + T('calc.part.' + id))}">−</button><output data-q="${id}">0</output><button type="button" class="cp-step-b" data-inc="${id}" aria-label="${esc(T('calc.more') + ': ' + T('calc.part.' + id))}">+</button></span>
        </div>`).join('');
    const sys = ['enamel', 'base_clear', 'tricoat'].map((s) => `<option value="${s}">${esc(T('calc.system.' + s))}</option>`).join('');
    const layer = (L) => `<fieldset class="cp-layer" data-layer="${L}"><legend>${esc(T('calc.layer.' + L))}</legend>
            <label class="cp-mlab"><span>${esc(T('calc.ratio'))}</span><input type="text" data-g="${L}.ratio" value="${esc(st.gun[L].ratio)}" autocomplete="off" spellcheck="false"></label>
            <label class="cp-mlab"><span>${esc(T('calc.vs'))}</span><input type="text" inputmode="decimal" autocomplete="off" data-g="${L}.vs" value="${st.gun[L].vs}"></label>
            <label class="cp-mlab"><span>${esc(T('calc.dft'))}</span><input type="text" inputmode="decimal" autocomplete="off" data-g="${L}.dft" value="${st.gun[L].dft}"></label>
        </fieldset>`;
    return `<div class="cp-seg" role="group" aria-label="${esc(T('calc.mode_label'))}">
            <button type="button" class="cp-seg-b" data-mode="can" aria-pressed="true">${esc(T('calc.mode_can'))}</button>
            <button type="button" class="cp-seg-b" data-mode="gun" aria-pressed="false">${esc(T('calc.mode_gun'))}</button>
        </div>
        <div class="cp-cf-h">${esc(T('calc.parts_title'))}</div>
        <div class="cp-parts">${parts}</div>
        <div class="cp-calc-row">
            <label class="cp-mlab"><span>${esc(T('calc.area_label'))}</span><input type="text" inputmode="decimal" autocomplete="off" data-c="area" placeholder="0"></label>
            <label class="cp-mlab"><span>${esc(T('calc.system_label'))}</span><select data-c="system">${sys}</select></label>
        </div>
        <div class="cp-hint">${esc(T('calc.area_hint'))}</div>
        <div data-for="can" class="cp-calc-row">
            <label class="cp-mlab"><span>${esc(T('calc.can_size'))}</span><select data-c="size">${st.can.sizes.map((v) => `<option value="${v}">${num(v, 0)} ${esc(T('calc.ml'))}</option>`).join('')}</select></label>
            <label class="cp-mlab"><span>${esc(T('calc.can_cover'))}</span><input type="text" inputmode="decimal" autocomplete="off" data-c="cover" value="${st.can.cover}"></label>
            <label class="cp-mlab"><span>${esc(T('calc.coats'))}</span><input type="text" inputmode="numeric" autocomplete="off" data-c="coats" value="${st.can.coats}"></label>
            <label class="cp-mlab" data-clear-coats><span>${esc(T('calc.clear_coats'))}</span><input type="text" inputmode="numeric" autocomplete="off" data-c="clearCoats" value="${st.can.clearCoats}"></label>
        </div>
        <div data-for="can" class="cp-hint">${esc(T('calc.can_cover_hint'))}</div>
        <div data-for="gun" hidden>
            <details class="cp-params"><summary>${esc(T('calc.gun_params'))}</summary>
                <div class="cp-hint">${esc(T('calc.gun_params_hint'))}</div>
                <label class="cp-mlab cp-te"><span>${esc(T('calc.te'))}</span><input type="text" inputmode="decimal" autocomplete="off" data-g="te" value="${st.gun.te}"></label>
                <div class="cp-layers">${['enamel', 'base', 'mid', 'clear'].map(layer).join('')}</div>
            </details>
        </div>
        <div class="cp-calc-res" data-res aria-live="polite"></div>
        <div class="cp-hint">${esc(T('calc.reserve_note'))}</div>

        <h3 class="cp-cf-h cp-mix-h">${esc(T('calc.mix_title'))}</h3>
        <div class="cp-calc-row">
            <label class="cp-mlab"><span>${esc(T('calc.mix_ratio'))}</span><input type="text" data-m="ratio" value="${esc(st.mix.ratio)}" autocomplete="off" spellcheck="false"></label>
            <label class="cp-mlab"><span>${esc(T('calc.mix_by'))}</span><select data-m="by">
                <option value="need">${esc(T('calc.mix_need'))}</option><option value="have">${esc(T('calc.mix_have'))}</option></select></label>
            <label class="cp-mlab"><span>${esc(T('calc.mix_amount'))}</span><input type="text" inputmode="decimal" autocomplete="off" data-m="amount" value="${st.mix.amount}"></label>
        </div>
        <div class="cp-mix-presets">${['2:1', '2:1 +10%', '3:1 +5%', '4:1', '4:1 +10%', '100:50:10'].map((r) => `<button type="button" class="cp-chip" data-ratio="${esc(r)}">${esc(r)}</button>`).join('')}</div>
        <div class="cp-hint">${esc(T('calc.mix_ratio_hint'))}</div>
        <div class="cp-pct" data-pct hidden>
            <span class="cp-hint">${esc(T('calc.mix_pct_of'))}</span>
            <label><input type="radio" name="cpPctOf" value="mix" checked> ${esc(T('calc.mix_pct_mix'))}</label>
            <label><input type="radio" name="cpPctOf" value="paint"> ${esc(T('calc.mix_pct_paint'))}</label>
        </div>
        <div class="cp-calc-res" data-mixres aria-live="polite"></div>
        <div class="cp-hint">${esc(T('calc.mix_weight'))} ${esc(T('calc.mix_pot'))}</div>`;
}

function layersOfSystem() {
    return st.system === 'tricoat' ? ['base', 'mid', 'clear'] : st.system === 'base_clear' ? ['base', 'clear'] : ['enamel'];
}

function renderResult(root) {
    const res = root.querySelector('[data-res]');
    const cc = root.querySelector('[data-clear-coats]');
    if (cc) cc.hidden = layersOfSystem().indexOf('clear') < 0;
    const A = area();
    for (const [id] of PARTS) { const o = root.querySelector(`[data-q="${id}"]`); if (o) o.textContent = String(st.qty[id] || 0); }
    const ai = root.querySelector('[data-c="area"]');
    if (ai && document.activeElement !== ai) ai.placeholder = A ? num(partsArea(), 2) : '0';
    if (!(A > 0)) { res.innerHTML = `<div class="cp-empty">${esc(T('calc.pick_parts'))}</div>`; return; }
    const layers = layersOfSystem();
    const lines = [`<div class="cp-res-area">${esc(T('calc.area_total'))}: <b>${num(A, 2)} ${esc(T('calc.m2'))}</b></div>`];
    if (st.mode === 'can') {
        const clearNeeded = layers.indexOf('clear') >= 0;
        for (const L of layers) {
            const coats = L === 'clear' ? st.can.clearCoats : st.can.coats;
            const c = cansFor(A, coats, st.can.cover);
            lines.push(`<div class="cp-res-line"><span>${esc(T('calc.layer.' + L))}</span><b>${c.cans} × ${num(st.can.size, 0)} ${esc(T('calc.ml'))}</b></div>`);
        }
        if (!clearNeeded) lines.push(`<div class="cp-hint">${esc(T('calc.enamel_can_note'))}</div>`);
    } else {
        const te = (st.gun.te || 65) / 100;
        lines.push(`<div class="cp-res-area">${esc(T('calc.gun_res_head'))}</div>`);
        for (const L of layers) {
            const p = st.gun[L];
            const l = sprayLitres(A, p.dft, p.vs, te);
            const m = mix(p.ratio, l * 1000);
            const names = COMPONENTS[L];
            const parts = m ? m.amounts.map((ml, i) => `${esc(T('calc.comp.' + (names[i] || 'other')))} ${litres(ml / 1000)}`).join(' · ') : esc(T('calc.mix_bad'));
            lines.push(`<div class="cp-res-line"><span>${esc(T('calc.layer.' + L))}</span><b>${litres(l)}</b></div><div class="cp-res-sub">${esc(T('calc.ready_mix'))}: ${parts}</div>`);
        }
    }
    res.innerHTML = lines.join('');
}

function renderMix(root) {
    const out = root.querySelector('[data-mixres]');
    const r = parseRatio(st.mix.ratio);
    root.querySelector('[data-pct]').hidden = !(r && r.pct);
    if (!r) { out.innerHTML = `<div class="cp-empty">${esc(T('calc.mix_bad'))}</div>`; return; }
    const m = mix(r, st.mix.amount, { pctOf: st.mix.pctOf, from: st.mix.by === 'have' ? 'first' : 'all' });
    if (!m) { out.innerHTML = ''; return; }
    const names = r.parts.length === 2 ? ['paint', 'hardener'] : r.parts.map((_, i) => ['paint', 'hardener', 'thinner', 'other'][i]);
    if (r.pct) names.push('thinner');
    const marks = cupMarks(m.amounts);
    const max = marks[marks.length - 1];
    const COL = ['var(--accent)', 'var(--warning, #fbbf24)', 'var(--success, #4ade80)', 'var(--text-muted)'];
    const rows = m.amounts.map((ml, i) => `<div class="cp-res-line"><span><i class="cp-swatch" style="background:${COL[i % 4]}"></i>${esc(T('calc.comp.' + names[i]))}</span><b>${num(roundMl(ml), 0)} ${esc(T('calc.ml'))}</b></div>`).join('');
    // the cup: stacked bands, the cumulative mark at each band's top
    const cup = `<div class="cp-cup" aria-hidden="true">${m.amounts.map((ml, i) => `<div class="cp-cup-band" style="height:${(ml / max * 100).toFixed(1)}%;background:${COL[i % 4]}"><span>${num(roundMl(marks[i]), 0)}</span></div>`).join('')}</div>`;
    out.innerHTML = `<div class="cp-mix-grid"><div>${rows}<div class="cp-res-line cp-res-total"><span>${esc(T('calc.mix_total'))}</span><b>${num(roundMl(m.total), 0)} ${esc(T('calc.ml'))}</b></div>
        <div class="cp-hint">${esc(T('calc.mix_cup'))} ${marks.map((x) => num(roundMl(x), 0)).join(' → ')} ${esc(T('calc.ml'))}</div></div>${cup}</div>`;
}

/* ── wiring ───────────────────────────────────────────────────────────────── */

let ROOT = null, sentCalc = 0, sentMix = 0;

function track(kind) {
    // one event per burst of typing, not per keystroke
    if (kind === 'calc') { clearTimeout(sentCalc); sentCalc = setTimeout(() => AE('feature_use', { action: 'calc', mode: st.mode, system: st.system, area: area(), parts: Object.keys(st.qty).filter((k) => st.qty[k]).join(',') }), 1500); }
    else { clearTimeout(sentMix); sentMix = setTimeout(() => { const r = parseRatio(st.mix.ratio); AE('feature_use', { action: 'mix', ratio: String(st.mix.ratio).slice(0, 16), ok: r ? 1 : 0, by: st.mix.by, pctOf: r && r.pct ? st.mix.pctOf : '' }); }, 1500); }
}

export function mountCalc(root, opts) {
    const o = opts || {};
    T = o.t || T; LOC = o.locale || LOC; AE = o.ae || AE;
    ROOT = root;
    root.innerHTML = formHtml();
    root.addEventListener('click', (e) => {
        const b = e.target.closest('button');
        if (!b) return;
        if (b.dataset.mode) {
            st.mode = b.dataset.mode;
            root.querySelectorAll('[data-mode]').forEach((x) => x.setAttribute('aria-pressed', x === b ? 'true' : 'false'));
            root.querySelectorAll('[data-for]').forEach((x) => { x.hidden = x.dataset.for !== st.mode; });
            renderResult(root); track('calc');
        } else if (b.dataset.inc || b.dataset.dec) {
            const id = b.dataset.inc || b.dataset.dec;
            st.qty[id] = Math.max(0, Math.min(20, (st.qty[id] || 0) + (b.dataset.inc ? 1 : -1)));
            st.area = null;
            const ai = root.querySelector('[data-c="area"]'); if (ai) ai.value = '';
            renderResult(root); track('calc');
        } else if (b.dataset.ratio) {
            st.mix.ratio = b.dataset.ratio;
            root.querySelector('[data-m="ratio"]').value = b.dataset.ratio;
            renderMix(root); track('mix');
        }
    });
    root.addEventListener('input', (e) => {
        const el = e.target;
        if (el.dataset.c === 'area') st.area = parseNum(el.value) > 0 ? parseNum(el.value) : null;
        else if (el.dataset.c === 'cover') st.can.cover = parseNum(el.value) > 0 ? parseNum(el.value) : canCover(st.can.size);
        else if (el.dataset.c === 'coats') st.can.coats = Math.max(1, Math.round(parseNum(el.value)) || DEFAULTS.can.coats);
        else if (el.dataset.c === 'clearCoats') st.can.clearCoats = Math.max(1, Math.round(parseNum(el.value)) || DEFAULTS.can.clearCoats);
        else if (el.dataset.g) {
            const [L, k] = el.dataset.g.split('.');
            if (L === 'te') st.gun.te = parseNum(el.value) > 0 ? Math.min(100, parseNum(el.value)) : DEFAULTS.gun.te;
            else st.gun[L][k] = k === 'ratio' ? el.value : (parseNum(el.value) > 0 ? parseNum(el.value) : DEFAULTS.gun[L][k]);
        } else if (el.dataset.m === 'ratio') { st.mix.ratio = el.value; renderMix(root); track('mix'); return; }
        else if (el.dataset.m === 'amount') { st.mix.amount = parseNum(el.value) > 0 ? parseNum(el.value) : 0; renderMix(root); track('mix'); return; }
        else if (el.name === 'cpPctOf') { st.mix.pctOf = el.value; renderMix(root); track('mix'); return; }
        else return;
        renderResult(root); track('calc');
    });
    root.addEventListener('change', (e) => {
        const el = e.target;
        if (el.dataset.c === 'system') { st.system = el.value; renderResult(root); track('calc'); }
        else if (el.dataset.c === 'size') {
            st.can.size = +el.value;
            st.can.cover = canCover(st.can.size);
            const ci = root.querySelector('[data-c="cover"]'); if (ci) ci.value = String(st.can.cover);
            renderResult(root); track('calc');
        }
        else if (el.dataset.m === 'by') { st.mix.by = el.value; renderMix(root); track('mix'); }
        else if (el.name === 'cpPctOf') { st.mix.pctOf = el.value; renderMix(root); track('mix'); }
    });
    renderResult(root);
    renderMix(root);
}

/** A colour's finish decides the paint system (metallic → base + clear). */
export function presetCalc(finish) {
    if (!ROOT) return;
    const L = layersFor(finish);
    st.system = L.length === 3 ? 'tricoat' : L[0] === 'base' ? 'base_clear' : 'enamel';
    const sel = ROOT.querySelector('[data-c="system"]');
    if (sel) sel.value = st.system;
    renderResult(ROOT);
}
